import {
  ArrowHeadLeftIcon,
  ArrowHeadRightIcon,
  Button,
  DatePicker,
  Grid,
  GridColumn,
  GridRow,
  Input,
  ModalBottomButtons,
  Switch,
  Tooltip,
} from '@bp/ui-components';
import dayjs from 'dayjs';
import { Form, Formik, FormikHelpers } from 'formik';
import { observer } from 'mobx-react-lite';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  getDaysDifference,
  isFirstBeforeSecond,
  isFirstBetweenSecondAndThird,
  isFirstSameOrAfterSecond,
  isFirstSameOrBeforeSecond,
  niceDate,
} from '../../../../utils/dateCalculations';
import { isHoliday } from '../../../../utils/typeguards';
import { HolidayType } from '../../../Holidays/graphql';
import { useTeachingBlockStore } from '../../Contexts/TeachingBlockContext';
import { TeachingBlockCard } from '../../TeachingBlockCard/TeachingBlockCard';
import { TeachingBlockCardType } from '../../types';
import styles from './CardDurationForm.module.scss';

type CardDurationFormProps = {
  card: TeachingBlockCardType;
  isVirtual: boolean;
  previous?: HolidayType | TeachingBlockCardType | null;
  next?: HolidayType | TeachingBlockCardType | null;
  onLoad: (loading: boolean) => void;
  onClose: () => void;
};

type CardDurationFormType = {
  start: Date;
  end: Date;
  split: boolean;
};

export const CardDurationForm = observer(
  ({ card, isVirtual, previous, next, onLoad, onClose }: CardDurationFormProps) => {
    const { t } = useTranslation();

    const [hint, setHint] = useState<string | null>(null);
    const [splitable, setSplitable] = useState<boolean>(false);

    const store = useTeachingBlockStore();

    const holidays = store.holidays;
    const schoolYear = store.schoolYear;

    const isActive = !!store.currentVersion.active;
    const today = dayjs().startOf('day');
    const todayInTimeframe = isFirstSameOrAfterSecond(store.schoolYear.start, today);
    const startInTimeframe = isActive && isFirstSameOrBeforeSecond(card.startDate, today);
    const previousBeforeToday =
      isActive &&
      !!previous &&
      isFirstBetweenSecondAndThird(isHoliday(previous) ? previous.end : previous?.endDate, today, card.endDate);

    function validate(values: CardDurationFormType): object {
      const errors: Record<string, string | null> = {};
      let message: string | null = null;

      const start = dayjs(values.start ?? card.startDate).startOf('day');
      const end = dayjs(values.end ?? card.endDate).startOf('day');

      if (previous) {
        if (isHoliday(previous)) {
          if (isFirstSameOrBeforeSecond(start, previous.end)) {
            message = t('validation.pinboard.startBeforeHolidayEnd');
            setSplitable(true);
          }
        } else {
          if (isFirstSameOrBeforeSecond(start, previous.endDate)) {
            errors.start = t('validation.pinboard.startBeforeCardEnd');
          }
        }
      }

      if (isActive && todayInTimeframe && !startInTimeframe && isFirstSameOrBeforeSecond(start, today)) {
        errors.start = t('validation.pinboard.startBeforeToday');
      }
      if (isFirstBeforeSecond(start, store.schoolYear.start)) {
        errors.start = t('validation.pinboard.startBeforeSchoolyearStart');
      }
      if (isFirstSameOrAfterSecond(start, end)) {
        errors.start = t('validation.pinboard.startAfterEnd');
      }

      if (next) {
        if (isHoliday(next)) {
          if (isFirstSameOrAfterSecond(end, next.start)) {
            message = t('validation.pinboard.endAfterHolidayStart');
            setSplitable(true);
          }
        } else {
          if (isFirstSameOrAfterSecond(end, next.startDate)) {
            errors.end = t('validation.pinboard.endAfterCardStart');
          }
        }
      }

      if (isActive && startInTimeframe && isFirstSameOrBeforeSecond(end, today)) {
        errors.end = t('validation.pinboard.endBeforeToday');
      }
      if (isFirstSameOrAfterSecond(end, dayjs(store.schoolYear.end).add(1, 'day'))) {
        errors.end = t('validation.pinboard.endAfterSchoolyearEnd');
      }
      if (end.isSameOrBefore(start, 'day')) {
        errors.end = t('validation.pinboard.endBeforeStart');
      }

      holidays.forEach((holiday) => {
        if (isFirstBetweenSecondAndThird(start, holiday.start, holiday.end)) {
          message = t('validation.pinboard.startInHoliday', { holiday: holiday.name });
          setSplitable(false);
        } else if (
          isFirstBetweenSecondAndThird(dayjs(card.startDate), holiday.start, holiday.end) &&
          isFirstSameOrBeforeSecond(start, holiday.end)
        ) {
          message = t('validation.pinboard.startBeforeHolidayEnd');
          setSplitable(true);
        }

        if (isFirstBetweenSecondAndThird(end, holiday.start, holiday.end)) {
          message = t('validation.pinboard.endInHoliday', { holiday: holiday.name });
          setSplitable(false);
        } else if (
          isFirstBetweenSecondAndThird(dayjs(card.endDate), holiday.start, holiday.end) &&
          isFirstSameOrAfterSecond(end, holiday.start)
        ) {
          message = t('validation.pinboard.endAfterHolidayStart');
          setSplitable(true);
        }
      });

      if (message) {
        setHint(`${t('common.hint')}: ${message}`);
      } else {
        setHint(null);
        setSplitable(false);
      }

      return errors;
    }

    function getHolidayDates(): { date: Date; holidayName: string }[] {
      const holidayDates: { date: Date; holidayName: string }[] = [];
      holidays.forEach((holiday) => {
        if (holiday.uuid === 'school-year-end-indicator') return;
        const difference = getDaysDifference(holiday.start, holiday.end);
        for (let i = 0; i < difference; i++) {
          holidayDates.push({
            date: dayjs(holiday.start).add(i, 'days').toDate(),
            holidayName: holiday.name,
          });
        }
      });
      return holidayDates;
    }

    function getMinMaxDates(values: CardDurationFormType) {
      const minDateStart = previous && !isHoliday(previous) ? dayjs(previous.endDate) : dayjs(schoolYear.start);
      const maxDateStart = next && !isHoliday(next) ? dayjs(next.startDate) : dayjs(schoolYear.end);
      const minDateEnd = dayjs(values.start ?? card.startDate).startOf('day');
      const maxDateEnd = dayjs(values.end ?? card.endDate).startOf('day');

      if (isActive) {
        return {
          minDateStart: (isFirstSameOrBeforeSecond(minDateStart, today)
            ? today.add(1, 'day')
            : minDateStart.add(1, 'day')
          ).toDate(),
          maxDateStart: (isFirstSameOrBeforeSecond(maxDateStart, today)
            ? today.subtract(1, 'day')
            : maxDateStart.subtract(1, 'day')
          ).toDate(),
          minDateEnd: (isFirstSameOrBeforeSecond(minDateEnd, today)
            ? today.add(1, 'day')
            : minDateEnd.add(1, 'day')
          ).toDate(),
          maxDateEnd: (isFirstSameOrBeforeSecond(maxDateEnd, today)
            ? today.subtract(1, 'day')
            : maxDateEnd.subtract(1, 'day')
          ).toDate(),
        };
      }

      return {
        minDateStart: minDateStart.add(1, 'day').toDate(),
        maxDateStart: maxDateStart.subtract(1, 'day').toDate(),
        minDateEnd: minDateEnd.add(1, 'day').toDate(),
        maxDateEnd: maxDateEnd.subtract(1, 'day').toDate(),
      };
    }

    const initialValues: CardDurationFormType = {
      start: dayjs(card.startDate).startOf('day').toDate(),
      end: dayjs(card.endDate).startOf('day').toDate(),
      split: true,
    };

    async function handleSubmit(values: CardDurationFormType, helpers: FormikHelpers<CardDurationFormType>) {
      onLoad(true);

      const startDateString = dayjs(values.start).format('YYYY-MM-DD');
      const endDateString = dayjs(values.end).format('YYYY-MM-DD');

      await store.updateCard(
        card,
        startDateString,
        endDateString,
        hint ? (splitable ? !values.split : true) : card.includeHoliday,
        isVirtual,
      );

      onLoad(false);
      helpers.resetForm();
      onClose();
    }

    return (
      <Formik<CardDurationFormType> initialValues={initialValues} onSubmit={handleSubmit} validate={validate}>
        {({ values, setFieldValue, resetForm, errors, dirty }) => {
          return (
            <Form className={styles['card-form']}>
              <Grid useFormGap className={styles['date-grid']}>
                <GridRow spacingBottom='none'>
                  <GridColumn width={2}>
                    {startInTimeframe ? (
                      <Input
                        name='start-before-today'
                        readonly
                        value={niceDate(card.startDate, 'medium')}
                        label={t('pinboard.edit.duration.startDate')}
                      />
                    ) : (!!previous && previousBeforeToday) || (!!previous && !isActive) ? (
                      isHoliday(previous) ? (
                        <Input
                          name='prev-holiday'
                          readonly
                          value={niceDate(previous.end, 'medium')}
                          label={t('pinboard.edit.duration.prevHolidayEnd')}
                        />
                      ) : (
                        <Input
                          name='prev-card'
                          readonly
                          value={niceDate(previous.endDate, 'medium')}
                          label={t('pinboard.edit.duration.prevCardEnd')}
                        />
                      )
                    ) : todayInTimeframe ? (
                      <Input
                        name='timeframe'
                        readonly
                        value={niceDate(today.toDate(), 'medium')}
                        label={t('pinboard.edit.duration.currentDate')}
                      />
                    ) : (
                      <Input
                        name='year-start'
                        readonly
                        value={niceDate(store.schoolYear?.start, 'medium')}
                        label={t('pinboard.edit.duration.schoolYearStart')}
                      />
                    )}
                  </GridColumn>

                  <GridColumn width={1}>
                    <Tooltip
                      triggerClass={styles.picker}
                      content={
                        startInTimeframe
                          ? t('pinboard.edit.duration.pickStartDisabledTooltip')
                          : t('pinboard.edit.duration.pickStartTooltip')
                      }
                    >
                      <Button
                        hierarchy='tertiary'
                        icon={<ArrowHeadRightIcon />}
                        disabled={startInTimeframe}
                        onClick={async () => {
                          const newStart = dayjs(
                            (!!previous && previousBeforeToday) || (!!previous && !isActive)
                              ? isHoliday(previous)
                                ? previous.end
                                : previous.endDate
                              : todayInTimeframe
                                ? undefined
                                : store.schoolYear?.start,
                          )
                            .startOf('day')
                            .add(1, 'day');
                          await setFieldValue('start', newStart.toDate());
                        }}
                      />
                    </Tooltip>
                  </GridColumn>

                  <GridColumn width={3}>
                    <DatePicker
                      holidays={getHolidayDates()}
                      holidayStyling='grey'
                      minDate={getMinMaxDates(values).minDateStart}
                      maxDate={dayjs(values.end).subtract(1, 'day').toDate()}
                      tooltipText={t('pinboard.edit.duration.fromHint')}
                      label={t('pinboard.edit.duration.start')}
                      name='start'
                      disabled={startInTimeframe}
                      onChange={async (value) => {
                        const newStart = dayjs(value).startOf('day');
                        await setFieldValue('start', newStart.toDate());
                      }}
                      value={values.start}
                      error={errors.start as string}
                    />
                  </GridColumn>
                  <GridColumn width={3}>
                    <DatePicker
                      holidays={getHolidayDates()}
                      holidayStyling='grey'
                      minDate={dayjs(values.start).add(1, 'day').toDate()}
                      maxDate={getMinMaxDates(values).maxDateStart}
                      tooltipText={t('pinboard.edit.duration.untilHint')}
                      label={t('pinboard.edit.duration.end')}
                      name='end'
                      onChange={async (value) => {
                        const newEnd = dayjs(value).startOf('day');
                        await setFieldValue('end', newEnd.toDate());
                      }}
                      value={values.end}
                      error={errors.end as string}
                    />
                  </GridColumn>

                  <GridColumn width={1}>
                    <Tooltip triggerClass={styles.picker} content={t('pinboard.edit.duration.pickEndTooltip')}>
                      <Button
                        hierarchy='tertiary'
                        icon={<ArrowHeadLeftIcon />}
                        onClick={async () => {
                          const newEnd = dayjs(
                            next ? (isHoliday(next) ? next.start : next.startDate) : store.schoolYear?.end,
                          )
                            .startOf('day')
                            .subtract(1, 'day');
                          await setFieldValue('end', newEnd.toDate());
                        }}
                      />
                    </Tooltip>
                  </GridColumn>

                  <GridColumn width={2}>
                    {next ? (
                      isHoliday(next) ? (
                        <Input
                          name='next-holiday'
                          readonly
                          value={niceDate(dayjs(next.start).toDate(), 'medium')}
                          label={
                            next.uuid === 'school-year-end-indicator'
                              ? t('pinboard.edit.duration.schoolYearEnd')
                              : t('pinboard.edit.duration.nextHolidayStart')
                          }
                        />
                      ) : (
                        <Input
                          name='next-card'
                          readonly
                          value={niceDate(next.startDate, 'medium')}
                          label={t('pinboard.edit.duration.nextCardStart')}
                        />
                      )
                    ) : (
                      <Input
                        name='year-end'
                        readonly
                        value={niceDate(store.schoolYear?.end, 'medium')}
                        label={t('pinboard.edit.duration.schoolYearEnd')}
                      />
                    )}
                  </GridColumn>
                </GridRow>

                <GridRow spacingTop='none'>
                  <GridColumn width={2} align='end'>
                    {startInTimeframe ? (
                      <div className={styles['start-preview']}>{t('pinboard.edit.duration.start')}</div>
                    ) : (!!previous && previousBeforeToday) || (!!previous && !isActive) ? (
                      isHoliday(previous) ? (
                        <div className={styles['holiday-preview']}>{previous.name}</div>
                      ) : (
                        <TeachingBlockCard className={styles['card-preview']} card={previous} passive />
                      )
                    ) : todayInTimeframe ? (
                      <div className={styles['today-preview']}>{t('common.today')}</div>
                    ) : (
                      <div className={styles['holiday-preview']}>
                        {t('pinboard.edit.duration.schoolYearStartHint', { year: store.schoolYear?.shortName })}
                      </div>
                    )}
                  </GridColumn>
                  <GridColumn offset='left' width={2}>
                    {next ? (
                      isHoliday(next) ? (
                        <div className={styles['holiday-preview']}>{next.name}</div>
                      ) : (
                        <TeachingBlockCard className={styles['card-preview']} card={next} passive />
                      )
                    ) : (
                      <div className={styles['holiday-preview']}>
                        {t('pinboard.edit.duration.schoolYearEndHint', { year: store.schoolYear?.shortName })}
                      </div>
                    )}
                  </GridColumn>
                </GridRow>
              </Grid>

              <ModalBottomButtons
                closeButton={{
                  callback: () => {
                    resetForm();
                    onClose();
                  },
                }}
                submitButton={{
                  disabled: !dirty || Object.values(errors).filter(Boolean).length > 0,
                }}
                additionalLeftComponent={
                  hint && (
                    <div className={styles.hint}>
                      <div className={styles.text}>{hint}</div>
                      {splitable && (
                        <Switch
                          size='small'
                          name='split'
                          onChange={async (e) => {
                            await setFieldValue('split', e.target.checked);
                          }}
                          checked={values.split}
                          label={t('teachingBlockVersion.timeConflict.split')}
                          className={styles.switch}
                        />
                      )}
                    </div>
                  )
                }
              />
            </Form>
          );
        }}
      </Formik>
    );
  },
);
